import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Broadcast, MapPin, Sparkle, ArrowRight } from '@phosphor-icons/react';

import { bubbleImageSrc } from '@/lib/bubbleImage';
/**
 * Public beacon scan landing — accessible via QR.
 * If not logged in, prompts user to claim an invite first.
 */
const BeaconScan = () => {
  const { code } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [beacon, setBeacon] = useState(null);
  const [busy, setBusy] = useState(false);
  const [claimed, setClaimed] = useState(null);

  useEffect(() => {
    api.get(`/beacons/scan/${code}`)
      .then(({ data }) => setBeacon(data.beacon))
      .catch(() => toast.error('beacon not found'));
    if (!user) localStorage.setItem('bx_pending_beacon', code);
  }, [code, user]);

  const claim = async () => {
    if (!user) {
      nav('/register');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/beacons/scan/${code}/claim`);
      setClaimed(data.bubble);
      localStorage.removeItem('bx_pending_beacon');
      if (navigator.vibrate) try { navigator.vibrate([60, 30, 200]); } catch {}
      toast.success(`${data.bubble.name} is yours`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'claim failed');
    } finally { setBusy(false); }
  };

  if (!beacon) {
    return <div className="min-h-screen grid place-items-center text-white/60">scanning…</div>;
  }

  if (claimed) {
    return (
      <div className="min-h-screen grid place-items-center px-4 py-10">
        <motion.div className="max-w-md w-full text-center"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}>
          <div className="text-[10px] uppercase tracking-[0.4em] text-bx-cyan flex items-center justify-center gap-2">
            <Sparkle size={12} weight="fill" /> beacon spawn complete
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">
            Meet <span className="shimmer-text">{claimed.name}</span>
          </h1>
          {bubbleImageSrc(claimed) && (
            <img src={bubbleImageSrc(claimed)}
                 className="w-56 h-56 object-cover rounded-full mx-auto my-7 ring-2 ring-bx-cyan/40 breathe"
                 alt={claimed.name} data-testid="beacon-spawned-bubble" />
          )}
          <p className="text-white/65 italic">"{claimed.personality?.bio || claimed.welcome_message}"</p>
          <button data-testid="beacon-go-bubble"
                  onClick={() => nav(`/bubble/${claimed.id}`)}
                  className="btn-primary px-7 py-3.5 rounded-full text-sm inline-flex items-center gap-2 mt-8">
            Take it with me <ArrowRight size={14} weight="bold" />
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10" data-testid="beacon-scan-page">
      <div className="max-w-md w-full text-center">
        <div className="text-[10px] uppercase tracking-[0.4em] text-bx-volt flex items-center justify-center gap-2">
          <Broadcast size={12} weight="fill" /> beacon · {beacon.code}
        </div>
        <h1 className="font-display text-4xl sm:text-6xl font-black tracking-tighter mt-2">
          {beacon.label}
        </h1>
        <div className="text-sm text-white/60 mt-3 flex items-center justify-center gap-1">
          <MapPin size={12} weight="fill" /> {beacon.location_name}
        </div>
        {beacon.description && (
          <p className="text-white/70 mt-4 italic max-w-sm mx-auto">"{beacon.description}"</p>
        )}

        <div className="relative my-10 h-44 w-44 mx-auto">
          {bubbleImageSrc(beacon) ? (
            <img src={bubbleImageSrc(beacon)}
                 alt={`${beacon.label} cover`}
                 className="absolute inset-0 rounded-full object-cover ring-2 ring-bx-volt/40 breathe"
                 data-testid="beacon-scan-cover" />
          ) : (
            <div className="absolute inset-6 rounded-full bg-gradient-to-br from-bx-volt via-bx-cyan to-bx-magenta breathe" />
          )}
          <span className="absolute inset-0 rounded-full border-2 border-bx-volt animate-ping pointer-events-none" />
          <span className="absolute inset-3 rounded-full border-2 border-bx-cyan animate-ping pointer-events-none" style={{ animationDelay: '0.4s' }} />
        </div>

        <div className="text-xs uppercase tracking-widest text-white/60 mb-4">
          {beacon.scans_count}/{beacon.max_scans} claimed
        </div>

        {!beacon.available ? (
          <div className="chip text-bx-magenta border-bx-magenta/40 mx-auto">
            {beacon.expired ? 'beacon expired' :
             beacon.scans_count >= beacon.max_scans ? 'all claimed' : 'inactive'}
          </div>
        ) : user ? (
          <button data-testid="beacon-claim" onClick={claim} disabled={busy}
                  className="btn-primary px-7 py-3.5 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-50">
            {busy ? 'Spawning…' : <>Claim the ambient bubble <ArrowRight size={14} weight="bold" /></>}
          </button>
        ) : (
          <div className="space-y-3">
            <Link to="/register" data-testid="beacon-signup"
                  className="btn-primary block px-7 py-3.5 rounded-full text-sm">
              Sign up to claim this bubble
            </Link>
            <Link to="/login" className="text-bx-cyan text-sm font-bold">
              Already a member? Sign in →
            </Link>
          </div>
        )}

        {beacon.welcome_message && beacon.available && (
          <div className="mt-8 glass rounded-2xl p-4 text-sm italic text-white/70">
            "{beacon.welcome_message}"
          </div>
        )}
      </div>
    </div>
  );
};

export default BeaconScan;
